import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  getProjects,
  getTemplates,
  getRecordings,
  generateDocument,
  Project,
  TemplateListItem,
} from '../services/api'
import { getClient } from '../lib/aperture'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(52, 211, 153, 0.15)',
}

const emeraldGlass = {
  background: 'rgba(6, 78, 59, 0.3)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(52, 211, 153, 0.2)',
}

export default function SOWBuilder() {
  const queryClient = useQueryClient()

  // State
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateListItem | null>(null)
  const [step, setStep] = useState<'project' | 'template' | 'generate'>('project')
  const [isGenerating, setIsGenerating] = useState(false)
  const [generationResult, setGenerationResult] = useState<{ success: boolean; message: string } | null>(null)

  // Fetch pre-sales projects only
  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects', 'pre_sales'],
    queryFn: async () => {
      const response = await getProjects()
      // Only show projects explicitly marked as pre_sales
      return response.data.filter((p) => p.workflow_type === 'pre_sales')
    },
  })

  // Fetch SOW templates
  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['templates', 'sow'],
    queryFn: async () => {
      const response = await getTemplates()
      // Filter to SOW templates
      return response.data.filter((t) =>
        ['sow', 'discovery-summary', 'proposal'].includes(t.use_case)
      )
    },
  })

  // Fetch recordings for selected project
  const { data: recordings } = useQuery({
    queryKey: ['recordings', selectedProject?.id],
    queryFn: () => getRecordings(selectedProject?.id).then((res) => res.data),
    enabled: !!selectedProject?.id,
  })

  // Generate document mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProject || !selectedTemplate) throw new Error('Missing selection')
      return generateDocument({
        project_id: selectedProject.id,
        template_name: selectedTemplate.name,
        output_format: 'docx',
      })
    },
    onSuccess: () => {
      getClient()?.capture('output_metric', { metric_key: 'sows_generated', value: 1 })
      setGenerationResult({
        success: true,
        message: 'SOW generation started! Check the Documents page for progress.',
      })
      queryClient.invalidateQueries({ queryKey: ['documents', selectedProject?.id] })
    },
    onError: (error: any) => {
      setGenerationResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to start SOW generation',
      })
    },
  })

  const handleGenerate = async () => {
    setIsGenerating(true)
    setGenerationResult(null)
    try {
      await generateMutation.mutateAsync()
    } finally {
      setIsGenerating(false)
    }
  }

  const transcriptCount = recordings?.filter((r) => r.transcript_id).length || 0

  // Step indicators
  const steps = [
    { id: 'project', label: 'Select Opportunity', icon: 'fa-building' },
    { id: 'template', label: 'Choose Template', icon: 'fa-file-contract' },
    { id: 'generate', label: 'Generate SOW', icon: 'fa-magic' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>
            <i className="fa-solid fa-file-contract mr-3" style={{ color: '#34d399' }}></i>
            SOW Builder
          </h1>
          <p className="text-sm mt-2" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            Generate Statements of Work from discovery call transcripts
          </p>
        </div>
        <span
          className="text-xs font-semibold px-3 py-1.5 rounded-full"
          style={{
            background: 'rgba(16, 185, 129, 0.2)',
            color: '#34d399',
            border: '1px solid rgba(52, 211, 153, 0.3)',
          }}
        >
          Pre-Sales Workflow
        </span>
      </div>

      {/* Progress Steps */}
      <div className="rounded-xl p-6" style={emeraldGlass}>
        <div className="flex items-center justify-between">
          {steps.map((s, index) => (
            <div key={s.id} className="flex items-center flex-1">
              <button
                onClick={() => {
                  if (s.id === 'project') setStep('project')
                  else if (s.id === 'template' && selectedProject) setStep('template')
                  else if (s.id === 'generate' && selectedProject && selectedTemplate) setStep('generate')
                }}
                className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-all ${
                  step === s.id ? 'scale-105' : 'opacity-60'
                }`}
                style={{
                  background: step === s.id ? 'rgba(16, 185, 129, 0.3)' : 'transparent',
                  border: step === s.id ? '1px solid rgba(52, 211, 153, 0.5)' : '1px solid transparent',
                }}
                disabled={
                  (s.id === 'template' && !selectedProject) ||
                  (s.id === 'generate' && (!selectedProject || !selectedTemplate))
                }
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{
                    background: step === s.id ? '#10b981' : 'rgba(52, 211, 153, 0.2)',
                    color: step === s.id ? '#ffffff' : '#34d399',
                  }}
                >
                  <i className={`fa-solid ${s.icon}`}></i>
                </div>
                <div className="text-left">
                  <div className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                    Step {index + 1}
                  </div>
                  <div className="text-sm font-medium" style={{ color: '#e2e8f0' }}>
                    {s.label}
                  </div>
                </div>
              </button>
              {index < steps.length - 1 && (
                <div
                  className="flex-1 h-0.5 mx-4"
                  style={{
                    background:
                      (s.id === 'project' && selectedProject) ||
                      (s.id === 'template' && selectedTemplate)
                        ? 'linear-gradient(90deg, #10b981, rgba(52, 211, 153, 0.3))'
                        : 'rgba(52, 211, 153, 0.2)',
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Select Project */}
      {step === 'project' && (
        <div className="rounded-xl p-6" style={glassPanel}>
          <h2 className="text-lg font-bold mb-4" style={{ color: '#e2e8f0' }}>
            <i className="fa-solid fa-building mr-2" style={{ color: '#34d399' }}></i>
            Select an Opportunity
          </h2>
          {projectsLoading ? (
            <div className="flex items-center justify-center h-32">
              <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#34d399' }}></i>
            </div>
          ) : projects && projects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => {
                    setSelectedProject(project)
                    setStep('template')
                  }}
                  className="text-left p-4 rounded-xl transition-all hover:scale-[1.02]"
                  style={{
                    ...glassPanel,
                    borderColor:
                      selectedProject?.id === project.id
                        ? 'rgba(52, 211, 153, 0.5)'
                        : 'rgba(52, 211, 153, 0.15)',
                    boxShadow:
                      selectedProject?.id === project.id
                        ? '0 0 20px rgba(16, 185, 129, 0.2)'
                        : 'none',
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        background: 'rgba(16, 185, 129, 0.2)',
                        border: '1px solid rgba(52, 211, 153, 0.3)',
                      }}
                    >
                      <i className="fa-solid fa-building" style={{ color: '#34d399' }}></i>
                    </div>
                    {selectedProject?.id === project.id && (
                      <i className="fa-solid fa-check-circle" style={{ color: '#34d399' }}></i>
                    )}
                  </div>
                  <h3 className="font-bold mb-1" style={{ color: '#e2e8f0' }}>
                    {project.customer_name}
                  </h3>
                  <p className="text-sm mb-2" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                    {project.project_name}
                  </p>
                  <div className="flex items-center space-x-3 text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    <span>
                      <i className="fa-solid fa-microphone mr-1"></i>
                      {project.recording_count} recordings
                    </span>
                    <span>
                      <i className="fa-solid fa-file mr-1"></i>
                      {project.document_count} docs
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <i className="fa-solid fa-folder-open text-4xl mb-4" style={{ color: 'rgba(52, 211, 153, 0.4)' }}></i>
              <p className="mb-4" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                No pre-sales opportunities found.
              </p>
              <Link
                to="/projects"
                className="px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center"
                style={{
                  background: 'rgba(16, 185, 129, 0.2)',
                  border: '1px solid rgba(52, 211, 153, 0.4)',
                  color: '#34d399',
                }}
              >
                <i className="fa-solid fa-plus mr-2"></i>
                Create Opportunity
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Select Template */}
      {step === 'template' && selectedProject && (
        <div className="space-y-6">
          {/* Selected Project Summary */}
          <div className="rounded-xl p-4" style={emeraldGlass}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{
                    background: 'rgba(16, 185, 129, 0.3)',
                    border: '1px solid rgba(52, 211, 153, 0.5)',
                  }}
                >
                  <i className="fa-solid fa-building text-xl" style={{ color: '#34d399' }}></i>
                </div>
                <div>
                  <h3 className="font-bold" style={{ color: '#e2e8f0' }}>
                    {selectedProject.customer_name}
                  </h3>
                  <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                    {selectedProject.project_name}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-2xl font-bold" style={{ color: '#34d399' }}>
                    {transcriptCount}
                  </div>
                  <div className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                    Transcripts
                  </div>
                </div>
                <button
                  onClick={() => setStep('project')}
                  className="px-3 py-1.5 rounded-lg text-sm"
                  style={{
                    background: 'rgba(148, 163, 184, 0.1)',
                    border: '1px solid rgba(148, 163, 184, 0.2)',
                    color: 'rgba(148, 163, 184, 0.7)',
                  }}
                >
                  Change
                </button>
              </div>
            </div>
          </div>

          {/* Template Selection */}
          <div className="rounded-xl p-6" style={glassPanel}>
            <h2 className="text-lg font-bold mb-4" style={{ color: '#e2e8f0' }}>
              <i className="fa-solid fa-file-contract mr-2" style={{ color: '#34d399' }}></i>
              Choose SOW Template
            </h2>
            {templatesLoading ? (
              <div className="flex items-center justify-center h-32">
                <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#34d399' }}></i>
              </div>
            ) : templates && templates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => {
                      setSelectedTemplate(template)
                      setStep('generate')
                    }}
                    className="text-left p-5 rounded-xl transition-all hover:scale-[1.02]"
                    style={{
                      ...glassPanel,
                      borderColor:
                        selectedTemplate?.id === template.id
                          ? 'rgba(52, 211, 153, 0.5)'
                          : 'rgba(52, 211, 153, 0.15)',
                      boxShadow:
                        selectedTemplate?.id === template.id
                          ? '0 0 20px rgba(16, 185, 129, 0.2)'
                          : 'none',
                    }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{
                          background: 'rgba(16, 185, 129, 0.2)',
                          border: '1px solid rgba(52, 211, 153, 0.3)',
                        }}
                      >
                        <i className="fa-solid fa-file-lines text-xl" style={{ color: '#34d399' }}></i>
                      </div>
                      {selectedTemplate?.id === template.id && (
                        <i className="fa-solid fa-check-circle" style={{ color: '#34d399' }}></i>
                      )}
                    </div>
                    <h3 className="font-bold mb-1" style={{ color: '#e2e8f0' }}>
                      {template.display_name}
                    </h3>
                    <p className="text-sm mb-3" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                      {template.description}
                    </p>
                    <div className="flex items-center space-x-2">
                      <span
                        className="text-xs px-2 py-1 rounded-full"
                        style={{
                          background: 'rgba(52, 211, 153, 0.2)',
                          color: '#34d399',
                        }}
                      >
                        {template.use_case.toUpperCase()}
                      </span>
                      <span
                        className="text-xs px-2 py-1 rounded-full"
                        style={{
                          background: 'rgba(148, 163, 184, 0.1)',
                          color: 'rgba(148, 163, 184, 0.7)',
                        }}
                      >
                        v{template.version || 1}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <i className="fa-solid fa-file-circle-question text-4xl mb-4" style={{ color: 'rgba(52, 211, 153, 0.4)' }}></i>
                <p style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                  No SOW templates available. Contact an admin to add templates.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Generate */}
      {step === 'generate' && selectedProject && selectedTemplate && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="rounded-xl p-6" style={emeraldGlass}>
            <h2 className="text-lg font-bold mb-4" style={{ color: '#e2e8f0' }}>
              <i className="fa-solid fa-clipboard-check mr-2" style={{ color: '#34d399' }}></i>
              Generation Summary
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs uppercase mb-2" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Opportunity
                </div>
                <div className="flex items-center space-x-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{
                      background: 'rgba(16, 185, 129, 0.2)',
                      border: '1px solid rgba(52, 211, 153, 0.3)',
                    }}
                  >
                    <i className="fa-solid fa-building" style={{ color: '#34d399' }}></i>
                  </div>
                  <div>
                    <div className="font-medium" style={{ color: '#e2e8f0' }}>
                      {selectedProject.customer_name}
                    </div>
                    <div className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                      {selectedProject.project_name}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase mb-2" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Template
                </div>
                <div className="flex items-center space-x-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{
                      background: 'rgba(16, 185, 129, 0.2)',
                      border: '1px solid rgba(52, 211, 153, 0.3)',
                    }}
                  >
                    <i className="fa-solid fa-file-lines" style={{ color: '#34d399' }}></i>
                  </div>
                  <div>
                    <div className="font-medium" style={{ color: '#e2e8f0' }}>
                      {selectedTemplate.display_name}
                    </div>
                    <div className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                      {selectedTemplate.use_case.toUpperCase()} Template
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-4" style={{ borderTop: '1px solid rgba(52, 211, 153, 0.2)' }}>
              <div className="flex items-center space-x-6">
                <div>
                  <div className="text-2xl font-bold" style={{ color: '#34d399' }}>
                    {transcriptCount}
                  </div>
                  <div className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    Discovery Transcripts
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-bold" style={{ color: '#2dd4bf' }}>
                    {selectedProject.document_count}
                  </div>
                  <div className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    Reference Documents
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <div className="rounded-xl p-6" style={glassPanel}>
            {generationResult ? (
              <div
                className="p-4 rounded-lg mb-4"
                style={{
                  background: generationResult.success
                    ? 'rgba(16, 185, 129, 0.1)'
                    : 'rgba(248, 113, 113, 0.1)',
                  border: generationResult.success
                    ? '1px solid rgba(52, 211, 153, 0.3)'
                    : '1px solid rgba(248, 113, 113, 0.3)',
                }}
              >
                <div className="flex items-center">
                  <i
                    className={`fa-solid ${generationResult.success ? 'fa-check-circle' : 'fa-exclamation-circle'} mr-3 text-xl`}
                    style={{ color: generationResult.success ? '#34d399' : '#f87171' }}
                  ></i>
                  <div>
                    <div className="font-medium" style={{ color: '#e2e8f0' }}>
                      {generationResult.success ? 'Generation Started!' : 'Generation Failed'}
                    </div>
                    <div className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                      {generationResult.message}
                    </div>
                  </div>
                </div>
                {generationResult.success && (
                  <div className="mt-4 flex space-x-3">
                    <Link
                      to={`/projects/${selectedProject.id}/documents`}
                      className="px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(20, 184, 166, 0.3) 100%)',
                        border: '1px solid rgba(52, 211, 153, 0.4)',
                        color: '#e2e8f0',
                      }}
                    >
                      <i className="fa-solid fa-file-lines mr-2"></i>
                      View Documents
                    </Link>
                    <button
                      onClick={() => {
                        setGenerationResult(null)
                        setSelectedProject(null)
                        setSelectedTemplate(null)
                        setStep('project')
                      }}
                      className="px-4 py-2 rounded-lg text-sm font-medium"
                      style={{
                        background: 'rgba(148, 163, 184, 0.1)',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        color: 'rgba(148, 163, 184, 0.7)',
                      }}
                    >
                      Start New SOW
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {transcriptCount === 0 && (
                  <div
                    className="p-4 rounded-lg mb-4"
                    style={{
                      background: 'rgba(251, 191, 36, 0.1)',
                      border: '1px solid rgba(251, 191, 36, 0.3)',
                    }}
                  >
                    <div className="flex items-center">
                      <i className="fa-solid fa-exclamation-triangle mr-3" style={{ color: '#fbbf24' }}></i>
                      <div>
                        <div className="font-medium" style={{ color: '#e2e8f0' }}>
                          No discovery transcripts found
                        </div>
                        <div className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                          Add recordings or import Teams calls before generating the SOW.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold" style={{ color: '#e2e8f0' }}>
                      Ready to Generate
                    </h3>
                    <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                      The AI will analyze your discovery transcripts and generate a complete SOW.
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setStep('template')}
                      className="px-4 py-2 rounded-lg text-sm font-medium"
                      style={{
                        background: 'rgba(148, 163, 184, 0.1)',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        color: 'rgba(148, 163, 184, 0.7)',
                      }}
                    >
                      Back
                    </button>
                    <button
                      onClick={handleGenerate}
                      disabled={isGenerating || transcriptCount === 0}
                      className="px-6 py-3 rounded-lg text-sm font-medium transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        border: '1px solid rgba(52, 211, 153, 0.5)',
                        color: '#ffffff',
                        boxShadow: '0 0 20px rgba(16, 185, 129, 0.3)',
                      }}
                    >
                      {isGenerating ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                          Generating...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-magic mr-2"></i>
                          Generate SOW
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
